import PropTypes from 'prop-types'
import React from 'react'

import ButtonCircle from 'components/ButtonCircle'
import injectSource from 'utils/client/injectSource'

import markerIcon from './assets/marker_purple.svg'
import { grayDarkStyle } from './mapStyle'
import {
  ButtonBox,
  GoogleMapContainer,
  GoogleMapControls,
  Wrapper,
} from './styles'

const GOOGLE_MAP_DOMAIN = 'https://maps.googleapis.com/maps/api/js'
const GOOGLE_MAP_API_KEY = 'AIzaSyApOC81lFA4eq8SR3diJy-DCPZ3WuIXv1I'

const GoogleMap = ({ coordinates, viewport, language }) => {
  const googleMapRef = React.useRef(null)
  const map = React.useRef(null)
  const marker = React.useRef(null)
  const [url, setUrl] = React.useState(
    () =>
      `${GOOGLE_MAP_DOMAIN}?key=${GOOGLE_MAP_API_KEY}&callback=googleMapInit&language=${language}`,
  )
  const [mapCenter, setMapCenter] = React.useState(
    () => coordinates[viewport.breakpoint],
  )

  React.useEffect(() => {
    setMapCenter(coordinates[viewport.breakpoint])
  }, [viewport, coordinates])

  React.useEffect(() => {
    setUrl(
      `${GOOGLE_MAP_DOMAIN}?key=${GOOGLE_MAP_API_KEY}&callback=googleMapInit&language=${language}`,
    )
  }, [language])

  React.useEffect(() => {
    if (mapCenter && map.current) {
      map.current.setCenter(mapCenter)
    }
  }, [mapCenter])

  const googleMapInit = React.useCallback(() => {
    const { lat, lng } = coordinates.marker

    const mapOptions = {
      zoom: 16,
      scrollwheel: false,
      navigationControl: false,
      mapTypeControl: false,
      scaleControl: false,
      disableDefaultUI: true,
      center: new window.google.maps.LatLng(mapCenter.lat, mapCenter.lng),
      styles: grayDarkStyle,
    }

    map.current = new window.google.maps.Map(googleMapRef.current, mapOptions)
    marker.current = new window.google.maps.Marker({
      position: new window.google.maps.LatLng({ lat, lng }),
      icon: markerIcon,
      map: map.current,
    })
  }, [coordinates, mapCenter])

  React.useEffect(() => {
    if (!map.current) {
      window.googleMapInit = googleMapInit
      injectSource(url, 'google-maps-script')
    }

    return () => {
      if ('google' in window && 'maps' in window.google) {
        const allScripts = document.querySelectorAll(
          'script[src*="maps.googleapis"]',
        )
        allScripts.forEach(item => item.parentNode.removeChild(item))

        window.google.maps = {
          __gjsload__: () => {},
        }
      }
    }
  }, [googleMapInit, url])

  const zoomInHandler = () => {
    if (map.current && map.current.getZoom() <= 20) {
      map.current.setZoom(map.current.getZoom() + 1)
    }
  }

  const zoomOutHandler = () => {
    if (map.current && map.current.getZoom() >= 10) {
      map.current.setZoom(map.current.getZoom() - 1)
    }
  }

  return (
    <Wrapper>
      <GoogleMapContainer ref={googleMapRef} />
      <GoogleMapControls>
        <ButtonBox>
          <ButtonCircle
            as="button"
            bgClassName="bg--gray--op-06"
            textClassName="text--white"
            icon="btn_plus"
            iconColor="black"
            wrapperProps={{ onClick: zoomInHandler }}
          />
        </ButtonBox>

        <ButtonBox>
          <ButtonCircle
            as="button"
            bgClassName="bg--gray--op-06"
            textClassName="text--white"
            icon="btn_minus"
            iconColor="black"
            wrapperProps={{ onClick: zoomOutHandler }}
          />
        </ButtonBox>
      </GoogleMapControls>
    </Wrapper>
  )
}

GoogleMap.propTypes = {
  /** Coordinates for all breakpoints and marker */
  coordinates: PropTypes.shape({
    marker: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    mobile: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    tablet: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    desktop: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    scaling: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
  }).isRequired,

  /** Language of map */
  language: PropTypes.string,

  /** Viewport object with current viewport */
  viewport: PropTypes.object, // eslint-disable-line react/no-unused-prop-types
}

GoogleMap.defaultProps = {
  viewport: {},
  language: '',
}

export default GoogleMap
